import type { Assessment } from './scoring';
import type { TaskId } from './types';

/**
 * Wie viele Aufnahmen einer Art an einer Aufgabe (oder an der Station) hängen.
 *
 * **Der `value` der Medienzeilen trägt seit dem 2026-09-14 die Anzahl**, nicht mehr den
 * Dateipfad (Festlegung des Nutzers, s. `docs/plan-medien-anzahl.md`). Der Server ordnet die
 * Dateien selbst zu, indem er die übertragenen Anhänge scannt; der Pfad in der Protokollzeile
 * half ihm nie und war bei einer weggeräumten Aufnahme sogar ein Verweis ins Leere.
 *
 * ⚠️ **Die Zahl ist der Gesamtstand, nicht ein Zuwachs.** Der Server wertet je (Item, Typ) nur
 * den **jüngsten** Eintrag aus. Drei Videos ergeben also nacheinander `"1"`, `"2"`, `"3"` — und
 * wird eines weggeräumt, `"2"`. Genau daran hängt der Fall, um dessentwillen das Ganze gebaut
 * wurde: eine archivierte Aufnahme, deren Zeile schon am Server stand, zeigte dort für immer
 * auf eine Datei, die nie ankam.
 *
 * ⚠️ **Eine Datei, eine Wahrheit.** Zwei Stellen schreiben Medienzeilen; liefen ihre Zählungen
 * auseinander, überschrieben sie einander mit verschiedenen Zahlen, und der Server behielte die
 * zufällig jüngste. Eigene Datei statt einer Funktion in `repos.ts`, damit die
 * Oberflächenmodule nicht die halbe Ablage mit in jeden Lauf ziehen — dieselbe Begründung wie
 * bei `domain/woerter.ts`.
 *
 * ## ⚠️ Eine Zusage an den Relay-Server
 *
 * **Zwei Zeilen mit demselben `creationDate` tragen denselben `value`.**
 *
 * Der Server löst den Gleichstand von `creationDate` **nicht** auf (`storeDate` wird dort
 * einmal je Anfrage gesetzt, nicht je Zeile — alle bis zu 200 Einträge eines Stapels tragen
 * denselben Wert). Er braucht es auch nicht: bei gleichem Stand ist es eine Dublette, und
 * welche er greift, ändert nichts. Am 2026-09-14 mit der Serversitzung so abgestimmt und vom
 * Nutzer entschieden.
 *
 * Die Zusage hält heute aus zwei voneinander unabhängigen Gründen:
 *
 * 1. **`AUDIO_/VIDEO_/IMAGE_COMMENT`** entstehen einzig in `repos.ts::saveAssessment`, und das
 *    läuft nur über den Autosave — **500 ms gedämpft** und in einer Kette, die zwei Läufe nie
 *    überlappen lässt (`persistence/autosave.ts`). Je Aufruf entsteht höchstens **eine** Zeile
 *    je (Typ, Ziel).
 * 2. **`HANDWRITING_COMMENT`** entsteht einzig in `features/comment/handwritingSave.ts`, beim
 *    Schliessen des Kommentarblatts — also in menschlichem Takt, und für einen **anderen Typ**
 *    als Weg 1. Die beiden Wege können sich im selben Schlüssel gar nicht begegnen.
 *
 * ⚠️ **Wer einen dritten Schreibweg baut, einen Stapel ohne Dämpfung einführt oder mehrere
 * Geräte zusammenführt, bricht die Zusage** — und muss es der Relay-Sitzung sagen. Dort liegt
 * die Antwort fertig bereit (`id DESC` im `ORDER BY`, samt Messung), sie ist nur bewusst nicht
 * gebaut. S. `memory/medien-anzahl-statt-pfad.md`.
 */

/**
 * Die Logtypen, deren `value` eine Anzahl trägt — in der Reihenfolge der Stammdaten
 * (`persistence/seed.ts`).
 *
 * ⚠️ **Die Markierebene steht bewusst nicht hier.** Sie gehört zu ihrem Foto (Entscheidung des
 * Nutzers, 2026-09-14): sie zählt nicht mit, bekommt keinen eigenen Typ und schreibt keine
 * eigene Zeile. Übertragen wird sie trotzdem — `server/anhaenge.ts` führt sie als eigene Art.
 */
export const MEDIEN_LOG_TYPEN = [
  'AUDIO_COMMENT',
  'VIDEO_COMMENT',
  'IMAGE_COMMENT',
  'HANDWRITING_COMMENT',
] as const;

export type MedienLogTyp = (typeof MEDIEN_LOG_TYPEN)[number];

/**
 * Die Typen, die aus einer **Liste** von Aufnahmen kommen und sich deshalb aus einem Vergleich
 * zweier Assessment-Stände ableiten lassen (`repos.ts::saveAssessment`).
 *
 * Die Handschrift fehlt hier: ihr Eintrag entsteht beim Schreiben der Datei
 * (`features/comment/handwritingSave.ts`), nicht aus einem späteren Vergleich — ihr Stand sagt
 * nichts darüber, ob die Datei zu diesem Zeitpunkt schon geschrieben ist.
 */
export const LISTEN_LOG_TYPEN = ['AUDIO_COMMENT', 'VIDEO_COMMENT', 'IMAGE_COMMENT'] as const;

export function medienAnzahl(a: Assessment, itemId: TaskId, typ: MedienLogTyp): number {
  const kasten = a.comments[itemId];
  if (!kasten) return 0;
  switch (typ) {
    case 'AUDIO_COMMENT':
      return kasten.audios.length;
    case 'VIDEO_COMMENT':
      return kasten.videos.length;
    case 'IMAGE_COMMENT':
      return kasten.photos.length;
    case 'HANDWRITING_COMMENT':
      // Eine Aufgabe trägt genau **eine** Handschriftseite; `handwriting` ist ihre Strichliste.
      // Die Striche zu zählen gäbe die Zahl der Federzüge, nicht die der Seiten.
      return kasten.handwriting.length > 0 ? 1 : 0;
  }
}
